/**
 * Note inspector (W2-06; DESIGN.md §4.11, §5.3, §2.4; rpe-format F§11).
 *
 * `NoteInspector` edits one note or many (MultiNoteSection wraps it with the counts header): the Guided basic
 * fields (type, time, end, X, side, fake) and the "More" fields (speed, height offset, width, opacity, appear
 * time, hitsound, note colour, hit-effect colour, judge width) with RPE >= 1.7 badges, mixed values, per-item
 * expressions in time order (seeded `rand` shown), units toggle (Full) and problem badges. In Guided the "More"
 * group starts collapsed but carries an "in use" badge when any of its values is not the default (disclosure
 * never hides data). A single note also shows the Phira line: simultaneous notes (multi-highlight) and when it
 * becomes visible at the current speed.
 */

import { useCallback, useLayoutEffect, useMemo, useRef, useState, type RefObject } from 'react';
import type { AppServices, InspectorSectionDef, InspectorSectionProps } from '../../../app/registry/types';
import { Core } from '../../../core/commands/index';
import type { ExprProgram } from '../../../core/expr/types';
import { Units } from '../../../core/model/units';
import type { AssetId, ChartDoc, DocIndexes, Hitsound, Note, NoteId, NoteKind } from '../../../core/model/types';
import { Beats } from '../../../core/time/beat';
import type { Beat } from '../../../core/time/types';
import { useT } from '../../../i18n/useT';
import type { MessageKey } from '../../../i18n/types';
import type { DocStore } from '../../../state/types';
import {
  Badge,
  BeatField,
  Button,
  Checkbox,
  ColorField,
  Field,
  IconButton,
  NumberField,
  Section,
  Segmented,
  Select,
} from '../../../ui';
import type { BadgeProps, FieldValue } from '../../../ui/types';
import {
  exprNotesShown,
  moveNotesBy,
  noteFieldFromShown,
  noteFieldToShown,
  setHoldEnds,
  setNotesTime,
  type NoteUnitField,
} from '../commands';
import { useDocValue, useTopicStamp } from '../hooks';
import { m } from '../inspector.messages';
import {
  aggregateNotes,
  arrayEq,
  lineLabel,
  mapField,
  type NoteAggregate,
  noteSec,
  NOTE_KINDS,
  simultaneousCount,
  soundAssets,
  sortedIds,
  sumAt,
} from '../model';
import {
  Actions,
  Callout,
  fmtNum,
  Readout,
  Row,
  Title,
  unitToggleFor,
  useBeatCtx,
  useSectionBase,
  useStore,
  WithInfo,
  type SectionBase,
} from '../parts';
import s from '../Inspector.module.css';
import { newSeed, playheadBeat, snapStep, useEditKit, type EditKit } from '../tx';
import { FIELD, useFieldProblems } from '../useFieldProblems';

// ---------------------------------------------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------------------------------------------

const KIND_LABEL = { tap: m.kindTap, drag: m.kindDrag, flick: m.kindFlick, hold: m.kindHold } as const;

/**
 * Compact segmented labels (UX-057, re-proof round 2). The option names stay readable while they fit the
 * inspector — the default 318 px shows "Tap Drag Flick Hold" — and the kit's icon-only variant (`iconOnly`:
 * icons plus the name as each item's tooltip) takes over exactly when a label would clip, so nothing is ever
 * cut mid-word. The width each group needs is measured while its labels are showing and re-compared on every
 * resize, so widening the inspector brings the words back. Labels are plain strings so the tooltip text is the
 * name itself, whatever the container width.
 */
function useCompactSegs(): [RefObject<HTMLDivElement | null>, boolean] {
  const ref = useRef<HTMLDivElement | null>(null);
  /** Content width each radiogroup needs for its labels, measured while they are visible. */
  const need = useRef<number[]>([]);
  const [compact, setCompact] = useState(false);
  useLayoutEffect(() => {
    const root = ref.current;
    if (root === null) return;
    const check = (): void => {
      let over = false;
      const groups = [...root.querySelectorAll<HTMLElement>('[role="radiogroup"]')];
      groups.forEach((g, i) => {
        const cs = getComputedStyle(g);
        const items = [...g.querySelectorAll<HTMLElement>('[role="radio"]')];
        const padX = parseFloat(cs.paddingLeft) + parseFloat(cs.paddingRight);
        const gap = parseFloat(cs.columnGap || '0');
        const labelsShown = g.getAttribute('data-icon-only') !== 'true';
        if (labelsShown) {
          // scrollWidth is useless here (the group is stretched), so add up the items and the gaps.
          const content =
            items.reduce((sum, it) => sum + it.getBoundingClientRect().width, 0) +
            Math.max(0, items.length - 1) * gap +
            padX;
          need.current[i] = Math.max(need.current[i] ?? 0, content);
          for (const lab of g.querySelectorAll<HTMLElement>('span')) {
            if (lab.scrollWidth > lab.clientWidth + 1) over = true;
          }
        } else {
          // The labels are hidden (1x1 clipped spans that always "overflow"): only the room decides, against
          // the width measured while they were showing.
          const needI = need.current[i] ?? 0;
          if (needI > g.clientWidth + 1) over = true;
        }
      });
      setCompact(over);
    };
    check();
    const ro = new ResizeObserver(check);
    ro.observe(root);
    return () => {
      ro.disconnect();
    };
  }, []);
  return [ref, compact];
}

const KIND_COUNT = { tap: m.countTap, drag: m.countDrag, flick: m.countFlick, hold: m.countHold } as const;
const KIND_ICON = {
  tap: 'RectangleHorizontal',
  drag: 'Minus',
  flick: 'ChevronsUp',
  hold: 'RectangleVertical',
} as const;

/** Value of a hitsound select ("default", "builtin:tap", "asset:<sha>"). */
export function hitsoundKey(h: Hitsound | null): string {
  if (h === null) return 'default';
  return h.kind === 'builtin' ? `builtin:${h.sound}` : `asset:${h.asset}`;
}

export function hitsoundFromKey(key: string): Hitsound | null {
  if (key === 'builtin:tap' || key === 'builtin:drag' || key === 'builtin:flick') {
    return { kind: 'builtin', sound: key.slice(8) as 'tap' | 'drag' | 'flick' };
  }
  if (key.startsWith('asset:')) return { kind: 'asset', asset: key.slice(6) as AssetId };
  return null;
}

/** Custom hitsounds the notes use that are not offered as sound assets (missing or other roles). */
function missingSounds(notes: readonly Note[], sounds: readonly { readonly id: AssetId }[]): AssetId[] {
  const known = new Set(sounds.map((a) => a.id));
  const out = new Set<AssetId>();
  for (const n of notes) if (n.hitsound?.kind === 'asset' && !known.has(n.hitsound.asset)) out.add(n.hitsound.asset);
  return [...out];
}

/** True when any "More" field of the notes differs from its default (the "in use" badge in Guided). */
export function moreInUse(notes: readonly Note[]): boolean {
  return notes.some(
    (n) =>
      n.speed !== 1 ||
      n.yOffset !== 0 ||
      n.size !== 1 ||
      n.alpha < 255 ||
      n.visibleTime !== null ||
      n.hitsound !== null ||
      n.tint !== null ||
      n.tintHitEffects !== null ||
      n.judgeArea !== null,
  );
}

function selectNotes(ids: readonly NoteId[]): (doc: ChartDoc) => Note[] {
  return (doc) => ids.map((id) => doc.notes.get(id)).filter((n): n is Note => n !== undefined);
}

/** Commits a number field over notes: a constant patches, an expression evaluates per note in time order. */
function noteNumberCommit(
  edit: EditKit,
  ids: readonly NoteId[],
  field: 'x' | 'speed' | 'yOffset' | 'visibleTime',
  fieldPath: string,
  seed: number,
  v: { readonly kind: 'number'; readonly value: number } | { readonly kind: 'expr'; readonly program: ExprProgram },
): void {
  const label = { key: m.txEditNotes, params: { field } };
  if (v.kind === 'number') {
    const value = v.value;
    edit.commit(fieldPath, label, (tx) => {
      Core.notes.patchNotes(tx, { ids, patch: { [field]: value } });
    });
  } else {
    const prog = v.program;
    edit.commit(fieldPath, label, (tx) => Core.notes.exprNotes(tx, { ids, field, prog, seed }));
  }
}

// ---------------------------------------------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------------------------------------------

export interface NoteInspectorProps {
  readonly services: AppServices;
  readonly ids: readonly NoteId[];
}

/** Note fields for one or several notes. */
export function NoteInspector(props: NoteInspectorProps) {
  const store = useStore(props.services);
  if (store === null || props.ids.length === 0) return null;
  return <NoteInspectorBody services={props.services} store={store} ids={props.ids} />;
}

function NoteInspectorBody(props: {
  readonly services: AppServices;
  readonly store: DocStore;
  readonly ids: readonly NoteId[];
}) {
  const { services, store, ids } = props;
  const t = useT();
  const base = useSectionBase(services, store);
  const beatCtx = useBeatCtx(services, base);
  const idsKey = ids.join(',');
  // Selector keyed on the ids' contents (the ids array is rebuilt on every render).
  const selector = useMemo(
    () => selectNotes(idsKey === '' ? [] : (idsKey.split(',').map(Number) as NoteId[])),
    [idsKey],
  );
  const notes = useDocValue(store, selector, ['notes'], arrayEq);
  const edit = useEditKit(services, store, `notes:${idsKey}`);
  const badges = useFieldProblems(services, { notes: ids });
  const [seed, setSeed] = useState(newSeed);
  // Segmented labels: readable names until they would clip, then icons with tooltips (UX-057).
  const [segRef, segCompact] = useCompactSegs();
  const live = useMemo(() => notes.map((n) => n.id), [notes]);
  // Every field in one pass, recomputed only when the notes change (50k-note selections stay responsive).
  const agg = useMemo(() => aggregateNotes(notes, hitsoundKey), [notes]);
  if (notes.length === 0) return null;
  const single = notes.length === 1 ? notes[0] : undefined;
  const afterExpr = (v: { readonly kind: string; readonly program?: ExprProgram }): void => {
    if (v.kind === 'expr' && v.program?.usesRand === true) setSeed(newSeed());
  };

  // ---- aggregated values
  const kind = agg.kind ?? { kind: 'mixed' as const };
  const time: FieldValue<Beat> = agg.time ?? { kind: 'value', value: Beats.ZERO };
  const holds = agg.holds;
  const end = agg.end;
  const x: FieldValue<number> = agg.x ?? { kind: 'value', value: 0 };
  const above = agg.above;
  const fake = agg.fake;

  return (
    <div className={s.fields} ref={segRef}>
      {/* ---------------- Type */}
      <Row edit={edit} field={FIELD.noteKind}>
        <Field
          label={t(m.noteType)}
          stacked
          helpId={FIELD.noteKind}
          badges={badges(FIELD.noteKind)}
          info={{
            text: { key: m.noteTypeInfo },
            rpeTerm: 'type 1 Tap / 2 Hold / 3 Flick / 4 Drag',
            communityTerm: 'Tap / Drag / Flick / Hold 键',
          }}
        >
          <Segmented<NoteKind>
            ariaLabel={t(m.noteType)}
            size="sm"
            iconOnly={segCompact ? true : undefined}
            value={kind.kind === 'mixed' ? 'mixed' : kind.value}
            options={NOTE_KINDS.map((k) => ({ value: k, label: t(KIND_LABEL[k]), icon: KIND_ICON[k] }))}
            onChange={(k) => {
              edit.once({ key: m.txNoteKind }, (tx) => {
                Core.notes.convertNotes(tx, { ids: live, kind: k, holdLen: snapStep(services) });
              });
            }}
          />
        </Field>
      </Row>

      {/* ---------------- Time / End */}
      <Row edit={edit} field={FIELD.noteTime}>
        <WithInfo info={{ text: m.noteTimeHelp, rpe: 'startTime' }}>
          <BeatField
            label={t(m.noteTime)}
            value={time}
            ctx={beatCtx}
            echo
            helpId={FIELD.noteTime}
            onCommit={(b, relative) => {
              edit.commit(FIELD.noteTime, { key: m.txNoteTime }, (tx) => {
                if (relative) moveNotesBy(tx, { ids: live, dt: b });
                else setNotesTime(tx, { ids: live, t: b });
              });
            }}
          />
        </WithInfo>
        <div className={s.inline}>
          {badges(FIELD.noteTime).map((b, i) => (
            <Badge key={i} {...b} />
          ))}
          <Button
            size="sm"
            variant="ghost"
            icon="Crosshair"
            onClick={() => {
              const at = playheadBeat(services, base.tempo);
              const earliest = agg.earliest ?? at;
              edit.once({ key: m.txNoteTime }, (tx) => {
                // Several notes move together: the earliest lands on the playhead, the pattern is kept.
                if (single !== undefined) setNotesTime(tx, { ids: live, t: at });
                else moveNotesBy(tx, { ids: live, dt: Beats.sub(at, earliest, tx.rounding) });
              });
            }}
          >
            {t(m.toPlayhead)}
          </Button>
          {single === undefined && <Readout>{t(m.toPlayheadMulti)}</Readout>}
        </div>
      </Row>
      {holds.length > 0 && end !== null && (
        <Row edit={edit} field={FIELD.noteEnd}>
          <WithInfo info={{ text: m.noteEndHelp, rpe: 'endTime' }}>
            <BeatField
              label={t(m.noteEnd)}
              value={end}
              ctx={beatCtx}
              echo
              helpId={FIELD.noteEnd}
              onCommit={(b, relative) => {
                const minLen = snapStep(services);
                const holdIds = holds.map((h) => h.id);
                edit.commit(FIELD.noteEnd, { key: m.txNoteEnd }, (tx) => {
                  setHoldEnds(tx, relative ? { ids: holdIds, dt: b, minLen } : { ids: holdIds, to: b, minLen });
                });
              }}
            />
          </WithInfo>
          {single?.kind === 'hold' && <HoldLength base={base} note={single} />}
        </Row>
      )}

      {/* ---------------- X */}
      <Row edit={edit} field={FIELD.noteX}>
        <WithInfo info={{ text: m.noteXInfo, rpe: 'positionX' }}>
          <NumberField
            label={t(m.noteX)}
            value={x}
            unit={t(m.px)}
            step={1}
            precision={3}
            vars="noteField"
            randomSeed={seed}
            help={{ key: m.noteXHelp }}
            helpId={FIELD.noteX}
            badges={badges(FIELD.noteX)}
            onCommit={(v) => {
              noteNumberCommit(edit, live, 'x', FIELD.noteX, seed, v);
              afterExpr(v);
            }}
            onScrub={(v, final) => {
              edit.scrub(
                FIELD.noteX,
                { key: m.txEditNotes, params: { field: 'x' } },
                (tx) => {
                  Core.notes.patchNotes(tx, { ids: live, patch: { x: v } });
                },
                final,
              );
            }}
          />
        </WithInfo>
      </Row>

      {/* ---------------- Side, Fake */}
      <Row edit={edit} field={FIELD.noteSide}>
        <Field
          label={t(m.noteSide)}
          helpId={FIELD.noteSide}
          help={{ key: m.noteSideHelp }}
          badges={badges(FIELD.noteSide)}
          info={{
            text: { key: m.noteSideInfo },
            rpeTerm: 'above 1 / 2',
            communityTerm: '上 / 下',
            illustration: 'side',
          }}
        >
          <Segmented<'top' | 'bottom'>
            ariaLabel={t(m.noteSide)}
            size="sm"
            iconOnly={segCompact ? true : undefined}
            value={above === null || above.kind === 'mixed' ? 'mixed' : above.value ? 'top' : 'bottom'}
            options={[
              { value: 'top', label: t(m.sideTop), icon: 'ArrowDownToLine' },
              { value: 'bottom', label: t(m.sideBottom), icon: 'ArrowUpToLine' },
            ]}
            onChange={(v) => {
              edit.once({ key: m.txNoteSide }, (tx) => {
                Core.notes.setSide(tx, { ids: live, above: v === 'top' });
              });
            }}
          />
        </Field>
      </Row>
      <Row edit={edit} field={FIELD.noteFake}>
        <Field
          label={t(m.noteFake)}
          helpId={FIELD.noteFake}
          badges={badges(FIELD.noteFake)}
          info={{ text: { key: m.noteFakeInfo }, rpeTerm: 'isFake', communityTerm: '假键', illustration: 'fake' }}
        >
          <Checkbox
            label={t(m.noteFakeLabel)}
            checked={fake}
            onChange={(v) => {
              edit.once({ key: m.txNoteFake }, (tx) => {
                Core.notes.setFake(tx, { ids: live, fake: v });
              });
            }}
          />
          <Readout>{t(m.noteFakeDesc)}</Readout>
        </Field>
      </Row>

      <NoteMore
        services={services}
        base={base}
        edit={edit}
        notes={notes}
        agg={agg}
        ids={live}
        seed={seed}
        badges={badges}
        segCompact={segCompact}
        onExpr={afterExpr}
      />

      {single !== undefined && <PhiraLine services={services} base={base} note={single} />}
    </div>
  );
}

function HoldLength(props: { readonly base: SectionBase; readonly note: Note }) {
  const t = useT();
  const { base, note } = props;
  const len = Beats.sub(note.end, note.t);
  const sec = base.tempo.secAt(note.end) - base.tempo.secAt(note.t);
  return <Readout>{t(m.holdLength, { beats: base.fmtBeat(len), sec: fmtNum(sec, 3) })}</Readout>;
}

// ---------------------------------------------------------------------------------------------------------------
// More (Full fields)
// ---------------------------------------------------------------------------------------------------------------

interface MoreProps {
  readonly services: AppServices;
  readonly base: SectionBase;
  readonly edit: EditKit;
  readonly notes: readonly Note[];
  readonly agg: NoteAggregate;
  readonly ids: readonly NoteId[];
  readonly seed: number;
  readonly badges: (field: string) => readonly BadgeProps[];
  /** Icon-only segmented labels (the name becomes the tooltip) when they would clip (UX-057). */
  readonly segCompact: boolean;
  readonly onExpr: (v: { readonly kind: string; readonly program?: ExprProgram }) => void;
}

function NoteMore(props: MoreProps) {
  const { services, base, edit, notes, agg, ids, seed, badges, segCompact } = props;
  const t = useT();
  const inUse = agg.moreInUse;
  const [open, setOpen] = useState(base.level === 'full' || inUse);
  const speed: FieldValue<number> = agg.speed ?? { kind: 'value', value: 1 };
  const yOffset: FieldValue<number> = agg.yOffset ?? { kind: 'value', value: 0 };
  const visible = agg.visibleTime;
  const allTimed = agg.allTimed;
  const anyTimed = agg.anyTimed;
  const appearsMode: 'always' | 'before' | 'mixed' = allTimed ? 'before' : anyTimed ? 'mixed' : 'always';
  const visibleSec = allTimed && visible !== null ? (visible as FieldValue<number>) : null;
  const hitsound = agg.hitsound;
  const kinds = agg.kind;
  const tint = agg.tint;
  const tintHit = agg.tintHit;
  const judgeSet = agg.judgeSet;
  const sounds = useDocValue(base.store, soundAssets, ['assets']);

  const unitField = (
    field: NoteUnitField,
    fieldPath: string,
    labelKey: MessageKey,
    unit: { readonly friendly: string; readonly raw: string },
    raw: FieldValue<number> | null,
    extra: {
      readonly help?: MessageKey;
      readonly info: { readonly text: MessageKey; readonly rpe: string };
      readonly rpe17?: boolean;
    },
  ) => {
    const rawMode = base.rawUnits;
    const value: FieldValue<number> =
      raw === null ? { kind: 'value', value: 0 } : mapField(raw, (v) => (rawMode ? v : noteFieldToShown(field, v)));
    const toRaw = (v: number): number =>
      rawMode ? (field === 'alpha' ? Math.round(v) : v) : noteFieldFromShown(field, v);
    const label = { key: m.txEditNotes, params: { field } };
    const fieldBadges: BadgeProps[] = [
      ...badges(fieldPath),
      ...(extra.rpe17 === true ? [{ kind: 'rpe17' as const }] : []),
    ];
    const toggle = unitToggleFor(services, base, unit.friendly, unit.raw);
    return (
      <Row edit={edit} field={fieldPath}>
        <WithInfo info={{ text: extra.info.text, rpe: extra.info.rpe }}>
          <NumberField
            label={t(labelKey)}
            value={value}
            step={rawMode && field !== 'alpha' ? 0.01 : 1}
            precision={3}
            vars="noteField"
            randomSeed={seed}
            helpId={fieldPath}
            badges={fieldBadges}
            min={0}
            {...(extra.help === undefined ? {} : { help: { key: extra.help } })}
            {...(toggle === undefined ? { unit: rawMode ? unit.raw : unit.friendly } : { unitToggle: toggle })}
            onCommit={(v) => {
              if (v.kind === 'number') {
                const r = toRaw(v.value);
                edit.commit(fieldPath, label, (tx) => {
                  Core.notes.patchNotes(tx, { ids, patch: { [field]: r } });
                });
              } else {
                const prog = v.program;
                edit.commit(fieldPath, label, (tx) =>
                  rawMode
                    ? Core.notes.exprNotes(tx, { ids, field, prog, seed })
                    : exprNotesShown(tx, { ids, field, prog, seed }),
                );
              }
              props.onExpr(v);
            }}
            onScrub={(v, final) => {
              const r = toRaw(v);
              edit.scrub(
                fieldPath,
                label,
                (tx) => {
                  Core.notes.patchNotes(tx, { ids, patch: { [field]: r } });
                },
                final,
              );
            }}
          />
        </WithInfo>
      </Row>
    );
  };

  const playSound = async (key: string): Promise<void> => {
    const h = hitsoundFromKey(key);
    const k = kinds?.kind === 'value' ? kinds.value : 'tap';
    await services.audio.ensureContext();
    if (h === null) {
      services.audio.playHitsound({ kind: 'skin', name: k === 'drag' ? 'drag' : k === 'flick' ? 'flick' : 'click' });
    } else if (h.kind === 'builtin') {
      services.audio.playHitsound({ kind: 'skin', name: h.sound === 'tap' ? 'click' : h.sound });
    } else {
      services.audio.playHitsound({ kind: 'asset', asset: h.asset });
    }
  };

  return (
    <Section
      title={<Title main={t(m.noteMore)} />}
      collapsible
      open={open}
      onOpenChange={setOpen}
      level={4}
      actions={base.level === 'guided' && inUse ? <Badge kind="inUse" tooltip={{ key: m.noteMoreInUse }} /> : undefined}
    >
      <div className={s.fields}>
        <Row edit={edit} field={FIELD.noteSpeed}>
          <WithInfo info={{ text: m.noteSpeedInfo, rpe: 'speed' }}>
            <NumberField
              label={t(m.noteSpeed)}
              value={speed}
              unit={t(m.times)}
              step={0.1}
              precision={3}
              vars="noteField"
              randomSeed={seed}
              help={{ key: m.noteSpeedHelp }}
              helpId={FIELD.noteSpeed}
              badges={badges(FIELD.noteSpeed)}
              onCommit={(v) => {
                noteNumberCommit(edit, ids, 'speed', FIELD.noteSpeed, seed, v);
                props.onExpr(v);
              }}
              onScrub={(v, final) => {
                edit.scrub(
                  FIELD.noteSpeed,
                  { key: m.txEditNotes, params: { field: 'speed' } },
                  (tx) => {
                    Core.notes.patchNotes(tx, { ids, patch: { speed: v } });
                  },
                  final,
                );
              }}
            />
          </WithInfo>
        </Row>
        <Row edit={edit} field={FIELD.noteYOffset}>
          <WithInfo info={{ text: m.noteYOffsetInfo, rpe: 'yOffset' }}>
            <NumberField
              label={t(m.noteYOffset)}
              value={yOffset}
              unit={t(m.px)}
              step={1}
              precision={3}
              vars="noteField"
              randomSeed={seed}
              help={{ key: m.noteYOffsetHelp }}
              helpId={FIELD.noteYOffset}
              badges={badges(FIELD.noteYOffset)}
              onCommit={(v) => {
                noteNumberCommit(edit, ids, 'yOffset', FIELD.noteYOffset, seed, v);
                props.onExpr(v);
              }}
              onScrub={(v, final) => {
                edit.scrub(
                  FIELD.noteYOffset,
                  { key: m.txEditNotes, params: { field: 'yOffset' } },
                  (tx) => {
                    Core.notes.patchNotes(tx, { ids, patch: { yOffset: v } });
                  },
                  final,
                );
              }}
            />
          </WithInfo>
        </Row>
        {unitField('size', FIELD.noteSize, m.noteWidth, { friendly: t(m.percent), raw: t(m.times) }, agg.size, {
          help: m.noteWidthHelp,
          info: { text: m.noteWidthInfo, rpe: 'size' },
        })}
        {unitField('alpha', FIELD.noteAlpha, m.noteOpacity, { friendly: t(m.percent), raw: '0-255' }, agg.alpha, {
          help: m.noteOpacityHelp,
          info: { text: m.noteOpacityHelp, rpe: 'alpha' },
        })}

        <Row edit={edit} field={FIELD.noteVisible}>
          <Field
            label={t(m.noteAppears)}
            stacked
            helpId={FIELD.noteVisible}
            help={{ key: m.noteAppearsHelp }}
            badges={badges(FIELD.noteVisible)}
            info={{ text: { key: m.noteAppearsInfo }, rpeTerm: 'visibleTime', communityTerm: '可见时间' }}
          >
            <Segmented<'always' | 'before'>
              ariaLabel={t(m.noteAppears)}
              size="sm"
              iconOnly={segCompact ? true : undefined}
              value={appearsMode}
              options={[
                { value: 'always', label: t(m.appearsAlways), icon: 'Eye' },
                { value: 'before', label: t(m.appearsBefore), icon: 'Timer' },
              ]}
              onChange={(v) => {
                edit.once({ key: m.txEditNotes, params: { field: 'visibleTime' } }, (tx) => {
                  if (v === 'always') Core.notes.patchNotes(tx, { ids, patch: { visibleTime: null } });
                  else {
                    for (const n of notes) {
                      if (n.visibleTime === null)
                        Core.notes.patchNotes(tx, { ids: [n.id], patch: { visibleTime: 1.5 } });
                    }
                  }
                });
              }}
            />
          </Field>
          {visibleSec !== null && (
            <NumberField
              label={t(m.noteAppearsSec)}
              value={visibleSec}
              unit={t(m.seconds)}
              step={0.1}
              min={0}
              precision={3}
              vars="noteField"
              randomSeed={seed}
              helpId={`${FIELD.noteVisible}.sec`}
              onCommit={(v) => {
                noteNumberCommit(edit, ids, 'visibleTime', FIELD.noteVisible, seed, v);
                props.onExpr(v);
              }}
              onScrub={(v, final) => {
                edit.scrub(
                  FIELD.noteVisible,
                  { key: m.txEditNotes, params: { field: 'visibleTime' } },
                  (tx) => {
                    Core.notes.patchNotes(tx, { ids, patch: { visibleTime: Math.max(0, v) } });
                  },
                  final,
                );
              }}
            />
          )}
        </Row>

        <Row edit={edit} field={FIELD.noteHitsound}>
          <Field
            label={t(m.noteHitsound)}
            helpId={FIELD.noteHitsound}
            badges={badges(FIELD.noteHitsound)}
            info={{ text: { key: m.hitsoundInfo }, rpeTerm: 'hitsound' }}
          >
            <div className={s.inline}>
              <div className={s.grow}>
                <Select<string>
                  ariaLabel={t(m.noteHitsound)}
                  value={hitsound === null || hitsound.kind === 'mixed' ? 'mixed' : hitsound.value}
                  options={[
                    {
                      value: 'default',
                      label: t(m.hitsoundDefault, { kind: kinds?.kind === 'value' ? kinds.value : 'tap' }),
                    },
                    { value: 'builtin:tap', label: t(m.hitsoundTap) },
                    { value: 'builtin:drag', label: t(m.hitsoundDrag) },
                    { value: 'builtin:flick', label: t(m.hitsoundFlick) },
                    ...sounds.map((a) => ({ value: `asset:${a.id}`, label: a.path, icon: 'FileAudio' })),
                    ...missingSounds(notes, sounds).map((id) => ({
                      value: `asset:${id}`,
                      label: t(m.hitsoundMissing, { path: base.store.doc.assets.get(id)?.path ?? id }),
                      icon: 'FileX',
                    })),
                    { value: 'add', label: t(m.hitsoundAdd), icon: 'Plus' },
                  ]}
                  onChange={(v) => {
                    if (v === 'add') {
                      void services.registry.run('assets.add', { role: 'hitsound' });
                      return;
                    }
                    const h = hitsoundFromKey(v);
                    edit.once({ key: m.txHitsound }, (tx) => {
                      Core.notes.setHitsound(tx, { ids, hitsound: h });
                    });
                  }}
                />
              </div>
              <IconButton
                icon="Volume2"
                size="sm"
                label={{ key: m.hitsoundPlay }}
                disabledReason={hitsound === null || hitsound.kind === 'mixed' ? { key: m.curveMixed } : null}
                onClick={() => {
                  if (hitsound?.kind === 'value') void playSound(hitsound.value);
                }}
              />
            </div>
          </Field>
        </Row>

        <Row edit={edit} field={FIELD.noteTint}>
          <WithInfo info={{ text: m.noteTintInfo, rpe: 'tint' }}>
            <ColorField
              className={s.colorStacked}
              label={t(m.noteTint)}
              value={tint}
              allowNone
              helpId={FIELD.noteTint}
              onCommit={(c) => {
                edit.commit(FIELD.noteTint, { key: m.txTint }, (tx) => {
                  Core.notes.patchNotes(tx, { ids, patch: { tint: c } });
                });
              }}
            />
          </WithInfo>
          <div className={s.badgeLine}>
            <Badge kind="rpe17" />
            {badges(FIELD.noteTint).map((b, i) => (
              <Badge key={i} {...b} />
            ))}
          </div>
        </Row>
        <Row edit={edit} field={FIELD.noteTintHit}>
          <WithInfo info={{ text: m.noteTintHitInfo, rpe: 'tintHitEffects' }}>
            <ColorField
              className={s.colorStacked}
              label={t(m.noteTintHit)}
              value={tintHit}
              allowNone
              helpId={FIELD.noteTintHit}
              onCommit={(c) => {
                edit.commit(FIELD.noteTintHit, { key: m.txTintHit }, (tx) => {
                  Core.notes.patchNotes(tx, { ids, patch: { tintHitEffects: c } });
                });
              }}
            />
          </WithInfo>
          <div className={s.badgeLine}>
            <Badge kind="rpe17" />
            {badges(FIELD.noteTintHit).map((b, i) => (
              <Badge key={i} {...b} />
            ))}
          </div>
        </Row>
        {unitField(
          'judgeArea',
          FIELD.noteJudge,
          m.noteJudge,
          { friendly: t(m.percent), raw: t(m.times) },
          agg.judgeArea,
          {
            help: m.noteJudgeHelp,
            info: { text: m.noteJudgeInfo, rpe: 'judgeArea' },
            rpe17: true,
          },
        )}
        {judgeSet && (
          <Actions>
            <Button
              size="sm"
              variant="ghost"
              icon="RotateCcw"
              onClick={() => {
                edit.once({ key: m.txEditNotes, params: { field: 'judgeArea' } }, (tx) => {
                  Core.notes.patchNotes(tx, { ids, patch: { judgeArea: null } });
                });
              }}
            >
              {t(m.resetDefault)}
            </Button>
          </Actions>
        )}
      </div>
    </Section>
  );
}

// ---------------------------------------------------------------------------------------------------------------
// Phira line (single note)
// ---------------------------------------------------------------------------------------------------------------

/** Reference viewport for the appear-time readout (the checker's 1920 x 1080). */
const REF_W = 1920;
const REF_H = 1080;

function PhiraLine(props: { readonly services: AppServices; readonly base: SectionBase; readonly note: Note }) {
  const { services, base, note } = props;
  const t = useT();
  const player = services.editor.getState().preview.player;
  const notesStamp = useTopicStamp(base.store, 'notes');
  const line = useDocValue(
    base.store,
    useCallback((doc: ChartDoc) => doc.lines.get(note.line), [note.line]),
    ['lines'],
  );
  const doc = base.store.doc;
  const others = useMemo(
    () => (notesStamp >= 0 ? simultaneousCount(doc, base.tempo, note) : 0),
    [notesStamp, doc, base.tempo, note],
  );
  const visible = useMemo(():
    { readonly kind: 'sec'; readonly sec: number } | { readonly kind: 'always' | 'never' | 'ui' } => {
    if (line?.attachUI != null) return { kind: 'ui' };
    const engine = services.engine;
    if (engine !== null) {
      try {
        const sec = engine.noteAppearSec(note.id, player, services.skins.active.metrics, REF_W, REF_H);
        if (sec === null) return { kind: 'never' };
        return Number.isFinite(sec) ? { kind: 'sec', sec } : { kind: 'always' };
      } catch {
        // The engine may not have compiled this note yet: fall back to the model estimate below.
      }
    }
    const hit = noteSec(base.tempo, note);
    const speed = sumAt(base.store.doc, base.store.idx, base.tempo, note.line, 'speed', hit) ?? 0;
    const cross = Units.crossScreenSec(speed * note.speed);
    const sec = note.visibleTime === null ? cross : Math.min(note.visibleTime, cross);
    if (!Number.isFinite(sec))
      return note.visibleTime === null ? { kind: 'never' } : { kind: 'sec', sec: note.visibleTime };
    return { kind: 'sec', sec };
  }, [line, services, player, note, base]);
  const vis =
    visible.kind === 'sec'
      ? t(m.phiraVisible, { sec: fmtNum(visible.sec, 2) })
      : visible.kind === 'always'
        ? t(m.phiraVisibleAlways)
        : visible.kind === 'ui'
          ? t(m.phiraUiMover)
          : t(m.phiraNeverVisible);
  return (
    <Callout tone={visible.kind === 'never' || visible.kind === 'ui' ? 'warning' : 'info'} testId="phira-line">
      {t(m.phiraLine, { simultaneous: t(m.phiraSimultaneous, { count: others }), visible: vis })}
    </Callout>
  );
}

// ---------------------------------------------------------------------------------------------------------------
// Section
// ---------------------------------------------------------------------------------------------------------------

/** One note. */
export function NoteSection(props: InspectorSectionProps) {
  const store = useStore(props.services);
  const ids = sortedIds(props.selection.notes);
  if (store === null || ids.length === 0) return null;
  return <NoteSectionBody services={props.services} store={store} ids={ids} />;
}

function NoteSectionBody(props: {
  readonly services: AppServices;
  readonly store: DocStore;
  readonly ids: readonly NoteId[];
}) {
  const { services, store, ids } = props;
  const t = useT();
  const first = ids[0];
  const note = useDocValue(
    store,
    useCallback((doc: ChartDoc) => (first === undefined ? undefined : doc.notes.get(first)), [first]),
    ['notes'],
  );
  const lineId = note?.line;
  const where = useDocValue(
    store,
    useCallback(
      (doc: ChartDoc, idx: DocIndexes) => (lineId === undefined ? '' : lineLabel(doc, idx, lineId)),
      [lineId],
    ),
    ['lines', 'lineOrder'],
  );
  if (note === undefined) return null;
  return (
    <Section
      title={<Title main={t(m.noteTitle, { kind: note.kind })} detail={where} />}
      actions={
        <>
          <IconButton icon="Copy" size="sm" label={{ key: m.copyNotes }} command="edit.copy" />
          <IconButton icon="Trash2" size="sm" label={{ key: m.deleteNotes }} command="edit.delete" />
        </>
      }
    >
      <NoteInspector services={services} ids={ids} />
    </Section>
  );
}

export const noteSectionDef: InspectorSectionDef = {
  id: 'note',
  order: 100,
  level: 'guided',
  appliesTo: (sel) => sel.notes.size === 1,
  component: NoteSection,
};

export { KIND_COUNT, selectNotes };
